import React, { useState } from 'react';
import { PUBLIC_PATH } from '../../../../../shared/config/routes-config';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import { trackEvent } from '../../../../../shared/services/utils/tracking/tracking.service';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { ConfirmModal } from '../../components/confirm-modal/confirm-modal.component';
import EdiText from 'react-editext';

export interface LandlordInfoProps {
  data: {
    landlordName: string;
    landlordId: string;
    landlordEmail: string;
    landlordPhone: string;
    propertyId: string;
    landlordShare: number;
    payouts: string[];
  };
  eventHandlers: {
    addLandLord: (payoutstoUpdate: string[]) => void;
    updateLandlordShare: (share: number) => void;
    deleteLandLord: () => void;
  };
}

const UserIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="User">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const MailIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Email">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Phone">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
);

const PercentIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-label="Percentage"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Add">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
    />
  </svg>
);

export const LandLordInfo: React.FC<LandlordInfoProps> = ({ data, eventHandlers }) => {
  const isMobileView = useWindowResizer();
  const [disabledActions, setDisabledActions] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');

  return (
    <div className="flex-1 mt-4 pb-8 rounded-lg bg-white shadow-md p-6 w-full">
      {data.landlordId ? (
        <>
          <div className="font-bold text-gray-800 font-gilroy-medium text-2xl mb-6 pb-3">
            Landlord Info
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex flex-col">
                <span className="text-base uppercase text-gray-500 font-medium">Name</span>
                <span className="text-gray-800 font-medium">{data.landlordName}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex flex-col">
                <span className="text-base uppercase text-gray-500 font-medium">Email</span>
                <span className="text-gray-800 font-medium">{data.landlordEmail}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex flex-col">
                <span className="text-base uppercase text-gray-500 font-medium">Phone</span>
                <span className="text-gray-800 font-medium">{data.landlordPhone}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex flex-col">
                <span className="text-base uppercase text-gray-500 font-medium">
                  Homeowner Share
                </span>
                <EdiText
                  className="text-gray-800 font-medium min-w-[80px]"
                  type="text"
                  onEditingStart={() => setDisabledActions(true)}
                  onCancel={() => setDisabledActions(false)}
                  value={data.landlordShare + '%'}
                  onSave={(val: string) => {
                    setDisabledActions(false);
                    eventHandlers.updateLandlordShare(Number(val.replace('%', '')));
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6 pt-4 border-t">
            <Button
              onClick={() => {
                if (data.payouts && data.payouts.length > 0) {
                  setShowConfirmModal(true);
                  setConfirmModalMessage(
                    'Do you want to update payouts assigned to this Homeowner also?'
                  );
                } else {
                  eventHandlers.addLandLord(false);
                }
              }}
              tracking={{
                action: 'cancel',
                category: '',
                label: ''
              }}
              size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              disabled={data.propertyId === '' || disabledActions}
            >
              Update Homeowner
            </Button>

            <Button
              onClick={() => setShowDeleteModal(true)}
              tracking={{
                action: 'open',
                category: 'blocked-date',
                label: 'add-blocked-date-modal'
              }}
              type={ButtonType.primaryInverted}
              size={ButtonSize.medium}
              disabled={disabledActions}
            >
              Delete Homeowner
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-10">
          <div className="w-20 h-20 mb-4 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
            <UserIcon />
          </div>
          <p className="text-gray-500 mb-4">No homeowner assigned</p>

          <Button
            onClick={() => {
              eventHandlers.addLandLord(false);
              trackEvent('add', {
                category: '',
                label: ''
              });
            }}
            tracking={{
              action: 'cancel',
              category: '',
              label: ''
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
            disabled={data.propertyId === '' || disabledActions}
          >
            <span className="flex items-center gap-2">
              <PlusIcon />
              Assign Homeowner
            </span>
          </Button>
          {data.propertyId === '' && (
            <label className="text-yellow-600 mt-4 text-sm">
              You can't assign a Homeowner to a unit that doesn't have property
            </label>
          )}
        </div>
      )}

      <ConfirmModal
        config={{ showModal: showConfirmModal, message: confirmModalMessage }}
        eventHandlers={{
          denyAction: () => {
            setShowConfirmModal(false);
            eventHandlers.addLandLord(false);
          },
          confirmAction: () => {
            setShowConfirmModal(false);
            eventHandlers.addLandLord(true);
            trackEvent('add', {
              category: '',
              label: ''
            });
          }
        }}
      />

      <ConfirmModal
        config={{
          showModal: showDeleteModal,
          message: 'Are you sure you want to delete this Homeowner?'
        }}
        eventHandlers={{
          denyAction: () => setShowDeleteModal(false),
          confirmAction: () => {
            setShowDeleteModal(false);
            eventHandlers.deleteLandLord();
          }
        }}
      />
    </div>
  );
};
